/**
 * FeedbackInvitePrompt — pokazuje ankietę gdy user wygeneruje N zdjęć.
 *
 * Triggeruje się po:
 *   - trigger1Jobs (domyślnie: 3) wygenerowanych zdjęć → pierwsze pokazanie
 *   - trigger2Jobs (domyślnie: 10) wygenerowanych zdjęć → drugie pokazanie
 *
 * Max 2 razy. Po dismiss: 7-dniowy cooldown (localStorage).
 * Po submit: 3 kredyty nagrody, nigdy nie pokazuje się ponownie.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { getFeedbackConfig, checkFeedbackGiven, submitFeedback } from "@/lib/apiClient";
import { fetchRailway } from "@/lib/apiClient";
import { Icon } from "@/components/ui/Icon";
import { Btn } from "@/components/ui/Btn";
import { toast } from "sonner";

const DISMISS_KEY = "feedback_invite_dismissed_at";
const SHOW_COUNT_KEY = "feedback_invite_shown_count";
const DISMISS_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;
const MAX_SHOWS = 2;

type Step = "invite" | "form" | "done";

async function fetchCompletedJobsCount(userId: string): Promise<number> {
  const res = await fetchRailway<{ count: number }>(`/v1/gallery/count?userId=${userId}`);
  if ("error" in res) return 0;
  return (res as { data: { count: number } }).data?.count ?? 0;
}

/**
 * `open`/`onOpenChange` są opcjonalne — pozwalają otworzyć ankietę z zewnątrz
 * (FeedbackBanner). Zewnętrzne `true` jest OR-owane z auto-triggerem, więc
 * podpięcie propsów NIE wyłącza pokazywania po N generacjach.
 */
export function FeedbackInvitePrompt({
  open: openProp,
  onOpenChange,
}: { open?: boolean; onOpenChange?: (v: boolean) => void } = {}) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [openInternal, setOpenInternal] = useState(false);
  const open = openProp || openInternal;
  const setOpen = useCallback(
    (v: boolean) => {
      onOpenChange?.(v);
      setOpenInternal(v);
    },
    [onOpenChange],
  );
  const [step, setStep] = useState<Step>("invite");
  const [q1, setQ1] = useState("");
  const [q2, setQ2] = useState("");
  const [q3, setQ3] = useState("");
  const triggeredRef = useRef(false);

  const configQuery = useQuery({
    queryKey: ["feedback-config"],
    queryFn: async () => {
      const res = await getFeedbackConfig();
      if ("error" in res) return { trigger1Jobs: 3, trigger2Jobs: 10, rewardCredits: 3, enabled: true };
      return res.data!;
    },
    staleTime: 10 * 60_000,
    enabled: !!user,
  });

  const givenQuery = useQuery({
    queryKey: ["feedback-given", user?.id],
    queryFn: async () => {
      const res = await checkFeedbackGiven();
      // Ten sam klucz cache co FeedbackBanner — fallback musi być identyczny.
      if ("error" in res) return { given: true }; // błąd → nie zaczepiaj usera
      return res.data!;
    },
    staleTime: 5 * 60_000,
    enabled: !!user,
  });

  const submitMutation = useMutation({
    mutationFn: () => submitFeedback({ q1, q2, q3: q3 || undefined }),
    onSuccess: (res) => {
      if ("error" in res) { toast.error("Błąd zapisu. Spróbuj ponownie."); return; }
      const credits = res.data?.creditsAwarded ?? 0;
      setStep("done");
      // Wspólny cache z FeedbackBanner — po submicie belka znika sama.
      queryClient.invalidateQueries({ queryKey: ["feedback-given"] });
      if (credits > 0) {
        // Bez tego toast obiecuje kredyty, a licznik w Topbarze pokazuje starą
        // wartość aż do następnego refetchu.
        queryClient.invalidateQueries({ queryKey: ["credits"] });
        toast.success(`+${credits} kredytów za wypełnienie ankiety!`);
      }
    },
  });

  useEffect(() => {
    if (!user || triggeredRef.current) return;
    if (!configQuery.data || givenQuery.data?.given) return;
    const config = configQuery.data;
    if (!config.enabled) return;

    const dismissedAt = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissedAt && Date.now() - dismissedAt < DISMISS_COOLDOWN_MS) return;

    const shownCount = Number(localStorage.getItem(SHOW_COUNT_KEY) || 0);
    if (shownCount >= MAX_SHOWS) return;

    (async () => {
      const jobsCount = await fetchCompletedJobsCount(user.id);
      const thresholds = [config.trigger1Jobs, config.trigger2Jobs].slice(0, MAX_SHOWS);

      // Sprawdź czy jesteśmy przy którymkolwiek progu (z tolerancją ±2 jobs)
      const hitThreshold = thresholds.some((t, idx) => {
        if (idx >= shownCount + 1) return false; // nie pomiń wcześniejszego
        return jobsCount >= t && jobsCount <= t + 2;
      });

      if (hitThreshold) {
        triggeredRef.current = true;
        localStorage.setItem(SHOW_COUNT_KEY, String(shownCount + 1));
        setOpen(true);
      }
    })();
  }, [user, configQuery.data, givenQuery.data, setOpen]);

  const handleDismiss = () => {
    setOpen(false);
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  };

  if (!open) return null;

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20,
    }} onClick={(e) => { if (e.target === e.currentTarget) handleDismiss(); }}>
      <div style={{
        background: "var(--surface)", border: "1px solid var(--border)",
        borderRadius: 20, padding: 28, maxWidth: 440, width: "100%",
        boxShadow: "var(--shadow-pop)",
      }}>
        {step === "invite" && (
          <>
            <div style={{ textAlign: "center", marginBottom: 20 }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>🎁</div>
              <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
                Pomożesz nam ulepszać AdresFlow?
              </h3>
              <p className="muted" style={{ fontSize: 14, lineHeight: 1.5 }}>
                15 sekund Twojego czasu — 3 pytania. W zamian doładujemy Ci{" "}
                <strong style={{ color: "var(--text)" }}>
                  {configQuery.data?.rewardCredits ?? 3} darmowe kredyty
                </strong>.
              </p>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <Btn variant="primary" block icon="check" onClick={() => setStep("form")}>
                Tak, pomogę
              </Btn>
              <Btn variant="ghost" block onClick={handleDismiss}>
                Może później
              </Btn>
            </div>
          </>
        )}

        {step === "form" && (
          <>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
              <button onClick={() => setStep("invite")} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-2)" }}>
                <Icon name="arrowL" style={{ width: 18, height: 18 }} />
              </button>
              <h3 style={{ fontSize: 16, fontWeight: 700 }}>3 krótkie pytania</h3>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div>
                <label className="field-lbl">1. Co najbardziej lubisz w AdresFlow?</label>
                <textarea className="input" rows={2} value={q1} onChange={(e) => setQ1(e.target.value)}
                  placeholder="np. szybkość, jakość zdjęć, łatwość obsługi…"
                  style={{ resize: "vertical", fontFamily: "inherit", width: "100%" }} />
              </div>
              <div>
                <label className="field-lbl">2. Co byś poprawił lub czego brakuje?</label>
                <textarea className="input" rows={2} value={q2} onChange={(e) => setQ2(e.target.value)}
                  placeholder="np. więcej stylów, lepsza galeria…"
                  style={{ resize: "vertical", fontFamily: "inherit", width: "100%" }} />
              </div>
              <div>
                <label className="field-lbl">3. Cokolwiek chcesz dodać? <span className="muted">(opcjonalne)</span></label>
                <textarea className="input" rows={2} value={q3} onChange={(e) => setQ3(e.target.value)}
                  placeholder="Dowolny komentarz…"
                  style={{ resize: "vertical", fontFamily: "inherit", width: "100%" }} />
              </div>
            </div>

            <div style={{ marginTop: 20 }}>
              <Btn
                variant="primary" block icon="check"
                disabled={!q1.trim() || !q2.trim() || submitMutation.isPending}
                onClick={() => submitMutation.mutate()}
              >
                {submitMutation.isPending ? "Zapisuję…" : "Wyślij i odbierz kredyty"}
              </Btn>
            </div>
          </>
        )}

        {step === "done" && (
          <div style={{ textAlign: "center", padding: "8px 0" }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🙌</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Dziękujemy!</h3>
            <p className="muted" style={{ fontSize: 14, marginBottom: 20 }}>
              Twoja opinia pomoże nam ulepszyć AdresFlow. Kredyty zostały dodane do Twojego konta.
            </p>
            <Btn variant="primary" block onClick={() => setOpen(false)}>Zamknij</Btn>
          </div>
        )}
      </div>
    </div>
  );
}
